// src/components/PublicWishlist.js
import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { standardUserApi } from "../services/standardUserApi";
import useNoIndex from "../hooks/useNoIndex";
import LoadingState from "./LoadingState";

/**
 * Public, no-account view of a subscriber's public wishlist items (see
 * WishlistPublicPage in models.py). A guest can express interest on any
 * item without an OkGather account - deliberately never the same thing as
 * booking an official gathering.
 */
const PublicWishlist = () => {
  const { token } = useParams();
  useNoIndex();

  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [interestedItemIds, setInterestedItemIds] = useState(new Set());
  const [activeItemId, setActiveItemId] = useState(null);
  const [guestName, setGuestName] = useState("");
  const [guestEmail, setGuestEmail] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const memberToken = localStorage.getItem("memberstackToken");

  useEffect(() => {
    const load = async () => {
      try {
        const data = await standardUserApi.getPublicWishlist(token);
        setPage(data);
      } catch (err) {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token]);

  const handleInterest = async (e, itemId) => {
    e.preventDefault();
    if (!memberToken && (!guestName.trim() || !guestEmail.trim())) {
      setErrorMessage("Please enter your name and email.");
      return;
    }
    setSubmitting(true);
    setErrorMessage("");
    try {
      await standardUserApi.registerWishlistInterest(
        token,
        itemId,
        { guestName: guestName.trim(), guestEmail: guestEmail.trim() },
        memberToken
      );
      setInterestedItemIds((prev) => new Set(prev).add(itemId));
      setActiveItemId(null);
    } catch (err) {
      console.error("PublicWishlist › interest error:", err);
      setErrorMessage("Couldn't register your interest right now. Please try again shortly.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <LoadingState />;
  }

  if (notFound || !page) {
    return (
      <div style={{ textAlign: "center", padding: "80px 20px" }}>
        <h4 style={{ color: "#111827" }}>This wishlist link isn't valid</h4>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: "560px", margin: "0 auto", padding: "48px 20px" }}>
      <p style={{ textAlign: "center", color: "#8B5FBF", fontWeight: "700", fontSize: "0.85rem", letterSpacing: "0.04em", textTransform: "uppercase", marginBottom: "8px" }}>
        {page.ownerFirstName}'s Wishlist
      </p>
      <h4 style={{ color: "#111827", fontSize: "1.6rem", fontWeight: "800", textAlign: "center", marginBottom: "24px" }}>
        Social ideas {page.ownerFirstName} wants to do
      </h4>

      {page.items.length === 0 ? (
        <p style={{ textAlign: "center", color: "#6B7280" }}>No public ideas yet.</p>
      ) : (
        page.items.map((item) => (
          <div key={item.id} style={{ border: "1.5px solid #E5E7EB", borderRadius: "12px", padding: "16px 20px", marginBottom: "12px" }}>
            <div style={{ color: "#111827", fontWeight: "700" }}>{item.title}</div>
            {item.description && <div style={{ color: "#6B7280", fontSize: "0.9rem", marginTop: "4px" }}>{item.description}</div>}
            <div style={{ color: "#9CA3AF", fontSize: "0.8rem", marginTop: "6px" }}>{item.interestCount} interested</div>

            {interestedItemIds.has(item.id) ? (
              <p style={{ color: "#15803D", fontWeight: "600", marginTop: "10px", fontSize: "0.9rem" }}>You're interested! 🎉</p>
            ) : activeItemId === item.id ? (
              <form onSubmit={(e) => handleInterest(e, item.id)} style={{ display: "flex", flexDirection: "column", gap: "8px", marginTop: "10px" }}>
                {!memberToken && (
                  <>
                    <input
                      type="text" placeholder="Your name" value={guestName}
                      onChange={(e) => setGuestName(e.target.value)}
                      style={{ padding: "8px 12px", borderRadius: "8px", border: "1.5px solid #E5E7EB", fontSize: "0.9rem" }}
                    />
                    <input
                      type="email" placeholder="Your email" value={guestEmail}
                      onChange={(e) => setGuestEmail(e.target.value)}
                      style={{ padding: "8px 12px", borderRadius: "8px", border: "1.5px solid #E5E7EB", fontSize: "0.9rem" }}
                    />
                  </>
                )}
                <button
                  type="submit" disabled={submitting}
                  style={{ backgroundColor: "#8B5FBF", color: "#fff", border: "none", borderRadius: "8px", padding: "8px 16px", fontWeight: "700", cursor: submitting ? "default" : "pointer", fontSize: "0.85rem" }}
                >
                  {submitting ? "Submitting..." : "Confirm interest"}
                </button>
              </form>
            ) : (
              <button
                onClick={() => setActiveItemId(item.id)}
                style={{ marginTop: "10px", backgroundColor: "#8B5FBF", color: "#fff", border: "none", borderRadius: "8px", padding: "8px 16px", fontWeight: "700", cursor: "pointer", fontSize: "0.85rem" }}
              >
                I'm interested
              </button>
            )}
          </div>
        ))
      )}

      {errorMessage && <p style={{ color: "#B91C1C", textAlign: "center", marginTop: "12px" }}>{errorMessage}</p>}

      <div style={{ textAlign: "center", marginTop: "32px" }}>
        <p style={{ color: "#6B7280", fontSize: "0.9rem", marginBottom: "12px" }}>
          OkGather is how {page.ownerFirstName} meets people for things like this.
        </p>
        <Link
          to="/"
          style={{ display: "inline-block", backgroundColor: "#8B5FBF", color: "#fff", borderRadius: "12px", padding: "12px 28px", fontWeight: "700", textDecoration: "none" }}
        >
          See OkGather's official events
        </Link>
      </div>
    </div>
  );
};

export default PublicWishlist;
